import React, { useMemo, useState, useRef } from 'react';
import useData from '../hooks/useData';
import GlassCard from '../components/ui/Card';
import GlassButton from '../components/ui/Button';
import { AddIcon, ProjectIcon, QuoteIcon, CalendarIcon } from '../components/ui/Icons';
import { getClientStatusPillClass } from '../utils/helpers';
import useClickOutside from '../hooks/useClickOutside';

// --- INÍCIO DA ALTERAÇÃO: Novo dropdown para o status do cliente ---
const StatusPillDropdown = ({ client, onStatusChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    useClickOutside(dropdownRef, () => setIsOpen(false));

    const statusOptions = ['Ativo', 'Inativo', 'Potencial'];

    const handleSelect = (status) => {
        onStatusChange({ ...client, status });
        setIsOpen(false);
    };

    return (
        <div ref={dropdownRef} className="relative z-10">
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
                className={`px-2 py-1 rounded-full text-xs transition-colors ${getClientStatusPillClass(client.status)} hover:ring-2 hover:ring-white/50`}
            >
                {client.status}
            </button>
            {isOpen && (
                <div className="absolute top-full right-0 mt-2 w-36 bg-gray-800/90 backdrop-blur-lg border border-white/10 rounded-lg shadow-xl p-1 animate-fade-in">
                    <div className="space-y-1">
                        {statusOptions.map(option => (
                            <button
                                key={option}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelect(option);
                                }}
                                className="w-full flex items-center gap-2 text-left px-3 py-1.5 rounded-md text-sm text-gray-200 transition-colors hover:bg-white/10"
                            >
                                <div className={`w-2 h-2 rounded-full ${getClientStatusPillClass(option).replace('bg-opacity-50', '')}`}></div>
                                <span>{option}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const ClientCard = ({ client, onClientClick, onStatusChange }) => {
    const { projects, quotes, tasks, currentUserProfile } = useData();

    const metrics = useMemo(() => {
        const associatedProjects = projects.filter(p => p.clientId === client.id);
        const associatedQuotes = quotes.filter(q => q.clientId === client.id && q.status === 'Aprovado');
        const associatedTasks = tasks.filter(t => t.client === client.name);

        const hourlyRate = (() => {
            if (!currentUserProfile || !currentUserProfile.workHoursPerMonth) return 0;
            const { monthlySalary = 4000, workHoursPerMonth = 160, costFactor = 1.5 } = currentUserProfile;
            return (monthlySalary * costFactor) / workHoursPerMonth;
        })();

        const totalValue = associatedQuotes.reduce((sum, quote) => {
            const quoteTotal = (quote.items || []).reduce((acc, item) => acc + (item.hours || 0) * (item.qty || 1) * hourlyRate, 0);
            return sum + quoteTotal;
        }, 0);

        const lastActivity = associatedTasks.reduce((latest, task) => {
            if (!task.deadline) return latest;
            const taskDate = new Date(task.deadline);
            return taskDate > latest ? taskDate : latest;
        }, new Date(0));

        return {
            projectCount: associatedProjects.length,
            totalValue,
            lastActivity: lastActivity > new Date(0) ? lastActivity.toLocaleDateString('pt-BR') : 'N/A'
        };
    }, [client, projects, quotes, tasks, currentUserProfile]);

    return (
        <GlassCard 
            className="flex flex-col justify-between cursor-pointer hover:bg-white/20 transition-all duration-200 hover:scale-[1.01]" 
            onClick={() => onClientClick(client)}
        >
            <div>
                <div className="flex justify-between items-start">
                    <h4 className="font-bold text-white text-lg pr-4">{client.name}</h4>
                    <StatusPillDropdown client={client} onStatusChange={onStatusChange} />
                </div>
                <p className="text-sm text-gray-300 mt-2 min-h-[20px]">{client.contact || 'Sem contato'}</p>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-center text-sm text-gray-300">
                <span className="flex items-center gap-1.5" title="Projetos"><ProjectIcon size={14}/> {metrics.projectCount}</span>
                <span className="flex items-center gap-1.5" title="Valor Aprovado"><QuoteIcon size={14}/> {metrics.totalValue.toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'})}</span>
                <span className="flex items-center gap-1.5" title="Última Atividade"><CalendarIcon size={14}/> {metrics.lastActivity}</span>
            </div>
        </GlassCard>
    );
};
// --- FIM DA ALTERAÇÃO ---

const ClientsList = ({ onAdd, onClientClick }) => {
    const { clients, handleSaveClient } = useData();

    return (
        <div className="p-4 space-y-4">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-white">Clientes</h2>
                <GlassButton onClick={onAdd} className="flex items-center space-x-2">
                    <AddIcon size={20} />
                    <span>Novo Cliente</span>
                </GlassButton>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {clients.map(client => (
                    <ClientCard 
                        key={client.id} 
                        client={client} 
                        onClientClick={onClientClick}
                        onStatusChange={handleSaveClient}
                    />
                ))}
            </div>
        </div>
    );
};

export default ClientsList;